import React, { useMemo } from 'react';
import { RadioGroup, FormControlLabel, Radio, Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization/useTranslation';
import * as StockSelectors from 'selectors/StockSelectors';
import { useSelector } from 'react-redux';
import ItemSelect from 'components/ItemSelect';
import { ProductApi } from 'common/types';

interface Props {
	entitledCategoryIds: string[] | null;
	entitledProductIds: string[] | null;
	onCategoryChange: (entitledCategoryIds: string[] | null) => void;
	onProductChange: (entitledProductIds: string[] | null) => void;
}

const noCategoryId = '__NO_CATEGORY__';

type DiscountCodeLimit = 'no-limit' | 'product' | 'category';

const DiscountLimits = (props: Props) => {
	const classes = useStyles();
	const { t, getTranslation } = useTranslation();
	const categoryIdMap = useSelector(StockSelectors.categoriesById);
	const categories = useSelector(StockSelectors.categoriesData);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const { entitledCategoryIds, entitledProductIds, onCategoryChange, onProductChange } = props;

	const categoryOptions = categories.map((category) => ({
		id: category.id,
		label: getTranslation(category.name),
	}));

	const productsByCategory = useMemo(
		() =>
			stockProducts.reduce((prev, product) => {
				if (!product.categoryIds?.length) {
					prev[noCategoryId] = prev[noCategoryId] ? [...prev[noCategoryId], product] : [product];
					return prev;
				}
				product.categoryIds.forEach((categoryId) => {
					prev[categoryId] = prev[categoryId] ? [...prev[categoryId], product] : [product];
				});
				return prev;
			}, {} as { [key: string]: ProductApi[] }),
		[stockProducts],
	);

	const productOptions = useMemo(() => {
		const optionsWithCategories = Object.entries(categoryIdMap).map(([categoryId, category]) => {
			return {
				name: getTranslation(category.name),
				options: (productsByCategory[categoryId] ?? []).map((product) => ({
					id: product.id,
					label: product.name.def,
				})),
			};
		});
		const noCategoryOption = {
			name: t('ProductCategories.noCategory'),
			options: (productsByCategory[noCategoryId] ?? []).map((product) => ({
				id: product.id,
				label: product.name.def,
			})),
		};
		return [...optionsWithCategories, noCategoryOption].filter((opt) => !!opt.options.length);
	}, [categoryIdMap, getTranslation, productsByCategory, t]);

	const discountCodeLimit: DiscountCodeLimit = !!entitledProductIds
		? 'product'
		: !!entitledCategoryIds
		? 'category'
		: 'no-limit';

	const handleLimitationTypeChange = (value: DiscountCodeLimit) => {
		if (value === 'no-limit') {
			onProductChange(null);
			onCategoryChange(null);
		}
		if (value === 'product') {
			onProductChange([]);
			onCategoryChange(null);
		}
		if (value === 'category') {
			onCategoryChange([]);
			onProductChange(null);
		}
	};

	return (
		<ContainerBox padding="normal">
			<Box mb={3}>
				<Typography variant="h6">{t('discount.appliesTo', 'Applies to')}</Typography>
			</Box>
			<RadioGroup
				className={classes.discountType}
				value={discountCodeLimit}
				onChange={(e) => handleLimitationTypeChange(e.target.value as DiscountCodeLimit)}
			>
				<FormControlLabel
					value={'no-limit'}
					control={<Radio color="primary" />}
					label={t('discount.entireOrder', 'Entire order')}
					labelPlacement="end"
				/>
				<FormControlLabel
					value={'product'}
					control={<Radio color="primary" />}
					label={t('discount.specificProducts', 'Specific products')}
					labelPlacement="end"
				/>
				{discountCodeLimit === 'product' && (
					<Box className={classes.itemSelectWrapper}>
						<ItemSelect
							value={entitledProductIds ?? []}
							options={productOptions}
							onChange={(ids: string[]) => {
								onProductChange(ids);
							}}
							chipLimit={5}
						/>
					</Box>
				)}
				<FormControlLabel
					value={'category'}
					control={<Radio color="primary" />}
					label={t('discount.specificCategories', 'Specific categories')}
					labelPlacement="end"
				/>
				{discountCodeLimit === 'category' && (
					<Box className={classes.itemSelectWrapper}>
						<ItemSelect
							value={entitledCategoryIds ?? []}
							options={categoryOptions}
							onChange={(ids: string[]) => {
								onCategoryChange(ids);
							}}
							chipLimit={5}
						/>
					</Box>
				)}
			</RadioGroup>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		discountType: {
			margin: theme.spacing(1, 0, 0, 0),
		},
		itemSelectWrapper: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(2),
			marginLeft: theme.spacing(4),
			maxWidth: `calc(100% - ${theme.spacing(4)})`,
		},
	}),
);

export default DiscountLimits;
